import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import debug from 'debug';

import {APIService} from './services/apiService';
import {AuthService} from './services/authService';
import {CacheService} from './services/cacheService';
import {GoogleAuthService} from './services/googleAuthService';
import {HttpService} from './services/httpService';
import {getStorageService} from './services/storageService';
import {UploadService} from './services/uploadService';
import {TranslationService} from './translation';
import LoggedTimer from './util/loggedTimer';
import {StructuredConsole} from './util/structured-logging';

import {
    AppConfigDataSource,
    AuthDataSource,
    ConfigDataSource,
    NotificationsDataSource,
    TagsDataSource,
    FeaturedGifsDataSource,
    GifsDataSource,
    RelatedGifsDataSource,
    PacksDataSource,
    CollectionsDataSource,
    ExploreTermsDataSource,
    SearchSuggestionsDataSource,
    SearchGifsByUsernameDataSource,
    SearchStickersByUsernameDataSource,
    SearchMemesByUsernameDataSource,
    ProfileDataSource,
    UniversalSearchDataSource,
} from './datasources';
import {
    GifMakerPageDataSource,
    GifEditorDataSource,
    ProfilePageDataSource,
} from './ui-datasources';

import {Store} from '../../replete';

import type {History} from 'history';
import type {Router} from 'inferno-router';
import type {FrontEndRequest, FrontEndResponse} from './util';

export type LocaleSuggestion = [string, string, string, string, string];

export class MyStore extends Store {
    declare console: StructuredConsole;
    request?: FrontEndRequest;
    httpService?: HttpService;
    authService?: AuthService;
    apiService?: APIService;

    constructor(createElement, console: StructuredConsole) {
        super(createElement);
        this.console = console;
    }

    destruct() {
        super.destruct();
        this.authService?.destruct();
        delete this.request;
        delete this.httpService;
        delete this.authService;
        delete this.apiService;
    }
}

type MyRouter = Router & {
    history: History;
};


export class AppGlobals {
    request: FrontEndRequest;
    response: FrontEndResponse;
    CONSTANTS: app.PublicConstants;
    PRIVATE_CONSTANTS: app.PrivateConstants;
    authService: AuthService|null;
    googleAuthService: GoogleAuthService|null;
    gtService: TranslationService|null;
    cacheService: CacheService|null;
    storageService: Storage|null;
    uploadService: UploadService|null;
    httpService: HttpService|null;
    apiService: APIService|null;
    loggedTimer: LoggedTimer|null;
    store?: MyStore|null;
    router: MyRouter|null;
    languages: LocaleSuggestion[];
    featureFlags: Record<string, boolean>;
    countryCode?: string;
    multivariateGroup?: number;
    multivariateGroupSelect?: (items: Record<string, any>,
        control?: any) => any;
    arianeMultivariateGroupSelect?: (items: Record<string, any>,
        control?: any) => any;
    GOOGLE_CONTACT_URL?: string;

    constructor(request: FrontEndRequest, response: FrontEndResponse,
        CONSTANTS: app.PublicConstants,
        PRIVATE_CONSTANTS: app.PrivateConstants) {
        this.request = request;
        this.response = response;
        this.CONSTANTS = CONSTANTS;
        this.PRIVATE_CONSTANTS = PRIVATE_CONSTANTS;
        this.authService = null;
        this.googleAuthService = null;
        this.gtService = null;
        this.cacheService = null;
        this.storageService = null;
        this.uploadService = null;
        this.httpService = null;
        this.apiService = null;
        this.loggedTimer = null;
        this.router = null;
        this.languages = [];
        this.featureFlags = {
            enableInternalLocales: false,
        };
    }
}

export function initConfig(val: app.AllConstants, PATH: string,
    request: FrontEndRequest, response: FrontEndResponse): jsx.AppGlobals {
    // This function is needed because these values can't be created in BaseApp,
    // since we need these appGlobals in `fetchData()` but BaseApp doesn't get
    // instantiated until `renderToString()`.
    const CONSTANTS = Object.assign({}, val.PUBLIC);
    const PRIVATE_CONSTANTS = Object.assign({}, val.PRIVATE);
    CONSTANTS.PATH = PATH;
    CONSTANTS.START_TS = Date.now();

    const appGlobals = new AppGlobals(
        request,
        response,
        CONSTANTS,
        PRIVATE_CONSTANTS,
    );
    appGlobals.gtService = new TranslationService();
    appGlobals.gtService.addTranslations(val.locale, val.localeData);
    appGlobals.gtService.gt.setLocale(val.locale);

    appGlobals.storageService = getStorageService(appGlobals);
    appGlobals.uploadService = new UploadService(appGlobals);
    appGlobals.googleAuthService = new GoogleAuthService(appGlobals);
    appGlobals.authService = new AuthService(appGlobals);
    appGlobals.cacheService = new CacheService(appGlobals);
    appGlobals.httpService = new HttpService(appGlobals);
    appGlobals.apiService = new APIService(appGlobals);
    appGlobals.loggedTimer = new LoggedTimer(appGlobals);
    appGlobals.authService.initWhenReady?.();

    const gettextSub = appGlobals.gtService.gettextSub;

    if (process.env.BROWSER) {
        const hostname = window.location.hostname;
        if (!hostname.match(/^\d+\.\d+\.\d+\.\d+(:\d+)?/) && hostname.indexOf('tenor.co') === -1) {
            // Check for tenor.co and tenor.com.
            // Prevent JS from running in Google and Bing cache views. If hosts
            // don't match, redirect should've happened on backend anyway.
            // This is defined here so that it can be reused.
            throw new Error(gettextSub('JS will not work in this environment, use the static HTML.'));
        }
    }

    const store = new MyStore((Target, props) => {
        return <Target {...props}/>;
    }, appGlobals.request.console.addIdentifier({
        component: 'replete',
    }));
    store.request = appGlobals.request;
    store.httpService = appGlobals.httpService;
    store.apiService = appGlobals.apiService;
    store.authService = appGlobals.authService;
    store.register('api.appConfig.*', AppConfigDataSource);
    store.register('api.auth', AuthDataSource);
    store.register('api.config.*', ConfigDataSource);
    store.register('api.notifications', NotificationsDataSource);
    store.register('api.tags.*', TagsDataSource);
    store.register('api.gifs.featured', FeaturedGifsDataSource);
    store.register('api.gifs.byId.*', GifsDataSource);
    store.register('api.gifs.related.*', RelatedGifsDataSource);
    store.register('api.gifs.searchByUsername.*', SearchGifsByUsernameDataSource);
    store.register('api.stickers.searchByUsername.*', SearchStickersByUsernameDataSource);
    store.register('api.memes.searchByUsername.*', SearchMemesByUsernameDataSource);
    store.register('api.universal.search.*', UniversalSearchDataSource);
    store.register('api.packs.byId.*', PacksDataSource);
    store.register('api.collections.byId.*', CollectionsDataSource);
    store.register('api.exploreterms.*', ExploreTermsDataSource);
    store.register('api.searchSuggestions.*', SearchSuggestionsDataSource);
    store.register('api.profiles.*', ProfileDataSource);

    store.register('ui.GifMakerPage.*', GifMakerPageDataSource);
    store.register('ui.GifEditor.*', GifEditorDataSource);
    store.register('ui.BaseProfilePage.*', ProfilePageDataSource);

    if (process.env.BROWSER) {
        const cacheEl = document.getElementById('store-cache');
        if (cacheEl && cacheEl.innerHTML.length) {
            store.set('api', JSON.parse(cacheEl.innerHTML));
        }
    }

    appGlobals.store = store;

    return appGlobals;
}
